import React from 'react';

import { type SingularKeys, Trans } from '@libs/i18n';

/**
 * Stage token → localized label for the chat "thinking…" indicator.
 *
 * Tokens come from three event streams emitted by the AI workflow:
 * - `file_ingest_progress` — upload pipeline (downloading / extracting / indexing)
 * - `assessment_progress`  — assessment pipeline (classifying / grading / summarizing)
 * - `step_progress`        — LangGraph chat nodes (rag_chat, workflow_intake,
 *                            greeting_intro, answer_question) and the Deep
 *                            Research route around the quality gate
 *
 * The `defaults` prop renders a polished English label even when the JSON
 * resource bundle hasn't been reloaded yet — translations are imported once
 * at module init in ``i18n-provider.tsx``, so dev-server HMR doesn't pick
 * up JSON edits without a full restart. The ``i18next-scanner`` config
 * (``defaultsKey: 'defaults'``) extracts these defaults automatically.
 */
export const STREAMING_STAGE_LABELS: Record<string, React.ReactNode> = {
  // File-ingest pipeline.
  downloading: <Trans i18nKey={'chat.streaming.stage.downloading' as SingularKeys} defaults="Downloading document…" />,
  extracting: <Trans i18nKey={'chat.streaming.stage.extracting' as SingularKeys} defaults="Extracting content…" />,
  indexing: <Trans i18nKey={'chat.streaming.stage.indexing' as SingularKeys} defaults="Indexing document…" />,
  // Assessment pipeline.
  assessment_started: <Trans i18nKey={'chat.streaming.stage.assessment_started' as SingularKeys} defaults="Starting assessment…" />,
  classifying: <Trans i18nKey={'chat.streaming.stage.classifying' as SingularKeys} defaults="Classifying document…" />,
  grading: <Trans i18nKey={'chat.streaming.stage.grading' as SingularKeys} defaults="Grading document…" />,
  summarizing: <Trans i18nKey={'chat.streaming.stage.summarizing' as SingularKeys} defaults="Summarizing document…" />,
  assessment_completed: <Trans i18nKey={'chat.streaming.stage.assessment_completed' as SingularKeys} defaults="Assessment complete…" />,
  // LangGraph chat node steps (standard + Deep Research).
  understanding_query: <Trans i18nKey={'chat.streaming.stage.understanding_query' as SingularKeys} defaults="Understanding your question…" />,
  retrieving_kb: <Trans i18nKey={'chat.streaming.stage.retrieving_kb' as SingularKeys} defaults="Exploring the knowledge base…" />,
  filtering_kb: <Trans i18nKey={'chat.streaming.stage.filtering_kb' as SingularKeys} defaults="Selecting the most relevant sources…" />,
  deep_researching: <Trans i18nKey={'chat.streaming.stage.deep_researching' as SingularKeys} defaults="Searching the latest public sources…" />,
  generating_response: <Trans i18nKey={'chat.streaming.stage.generating_response' as SingularKeys} defaults="Composing your answer…" />,
  quality_check: <Trans i18nKey={'chat.streaming.stage.quality_check' as SingularKeys} defaults="Reviewing answer quality…" />,
  finalizing_answer: <Trans i18nKey={'chat.streaming.stage.finalizing_answer' as SingularKeys} defaults="Finalizing your answer…" />,
};

export const STREAMING_STAGE_FALLBACK: React.ReactNode = (
  <Trans i18nKey={'chat.streaming.stage.processing' as SingularKeys} defaults="Thinking…" />
);
